import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, UserX } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/content/EmptyState";
import {
  useAdminUserDetail,
  useSetUserRole,
  useSetUserStatus,
} from "@/hooks/useAdmin";
import { formatDate, formatRupees } from "@/lib/format";
import { RoleBadge, StatusBadge } from "@/pages/admin/adminBadges";
import { USER_ROLES } from "@/pages/admin/adminConstants";
import type { PaymentRecord, SubscriptionRecord, User } from "@/lib/api-types";

// The handler only accepts these three for a status change (SUSPENDED is a
// filter-only value, not a settable one via /status).
const SETTABLE_STATUSES: User["user_status"][] = ["ACTIVE", "INACTIVE", "DELETED"];

const selectClass =
  "h-10 w-full rounded-lg border border-border bg-surface dark:bg-surface-2 px-3 text-sm text-text " +
  "transition-[border-color,box-shadow] duration-150 hover:border-border-strong " +
  "focus-visible:outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-ring/40";

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="text-sm text-text-muted">{label}</dt>
      <dd className="text-right text-sm font-medium text-text">{value || "--"}</dd>
    </div>
  );
}

function StatusControl({ user }: { user: User }) {
  const [next, setNext] = useState<User["user_status"]>(
    SETTABLE_STATUSES.includes(user.user_status) ? user.user_status : "ACTIVE",
  );
  const mutation = useSetUserStatus();
  const changed = next !== user.user_status;

  const onApply = () => {
    if (!changed) return;
    const ok = window.confirm(
      `Change status of ${user.email} from ${user.user_status} to ${next}?`,
    );
    if (!ok) return;
    mutation.mutate(
      { user_id: user.user_id, status: next },
      {
        onSuccess: () => toast.success(`Status updated to ${next}`),
        onError: () => toast.error("Failed to update status"),
      },
    );
  };

  return (
    <div className="flex items-end gap-2">
      <label className="flex flex-1 flex-col gap-1 text-xs font-medium text-text-muted">
        Status
        <select
          value={next}
          onChange={(e) => setNext(e.target.value as User["user_status"])}
          className={selectClass}
        >
          {SETTABLE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      <Button
        variant={next === "DELETED" ? "danger" : "secondary"}
        disabled={!changed}
        loading={mutation.isPending}
        onClick={onApply}
      >
        Apply
      </Button>
    </div>
  );
}

function RoleControl({ user }: { user: User }) {
  const [next, setNext] = useState<User["role_id"]>(user.role_id);
  const mutation = useSetUserRole();
  const changed = next !== user.role_id;

  const onApply = () => {
    if (!changed) return;
    const ok = window.confirm(
      `Change role of ${user.email} from ${user.role_id} to ${next}?`,
    );
    if (!ok) return;
    mutation.mutate(
      { user_id: user.user_id, role_id: next },
      {
        onSuccess: () => toast.success(`Role updated to ${next}`),
        onError: () => toast.error("Failed to update role"),
      },
    );
  };

  return (
    <div className="flex items-end gap-2">
      <label className="flex flex-1 flex-col gap-1 text-xs font-medium text-text-muted">
        Role
        <select
          value={next}
          onChange={(e) => setNext(e.target.value as User["role_id"])}
          className={selectClass}
        >
          {USER_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>
      <Button
        variant="secondary"
        disabled={!changed}
        loading={mutation.isPending}
        onClick={onApply}
      >
        Apply
      </Button>
    </div>
  );
}

function SubscriptionRow({ sub }: { sub: SubscriptionRecord }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border py-2 last:border-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-text">
          {sub.class} -- Annual Plan
        </p>
        <p className="text-xs text-text-muted">
          {formatDate(sub.start_date)} - {formatDate(sub.end_date)}
        </p>
      </div>
      <div className="text-right">
        <Badge tone={sub.status === "ACTIVE" ? "success" : "neutral"}>{sub.status}</Badge>
        <p className="mt-1 text-xs text-text-muted tabular-nums">
          {formatRupees(sub.amount_paid / 100)}
        </p>
      </div>
    </div>
  );
}

function PaymentRow({ payment }: { payment: PaymentRecord }) {
  const tone =
    payment.status === "VERIFIED"
      ? "success"
      : payment.status === "FAILED"
        ? "danger"
        : "neutral";
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border py-2 last:border-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-text tabular-nums">
          {formatRupees(payment.amount / 100)}
        </p>
        <p className="text-xs text-text-muted">
          {payment.created_at ? formatDate(payment.created_at) : "--"}
        </p>
      </div>
      <Badge tone={tone}>{payment.status}</Badge>
    </div>
  );
}

export function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const { data, isLoading, isError } = useAdminUserDetail(userId);

  const backLink = (
    <Link
      to="/admin/users"
      className="mb-5 inline-flex items-center gap-1.5 text-sm text-text-muted transition-colors hover:text-text"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to users
    </Link>
  );

  if (isLoading) {
    return (
      <div>
        {backLink}
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        {backLink}
        <EmptyState icon={UserX} title="User not found" body="This user could not be loaded." />
      </div>
    );
  }

  const { user, subscriptions, recent_payments, family_links } = data;
  const activeSubs = subscriptions.filter((s) => s.status === "ACTIVE");
  const familyCount = (family_links?.children.length ?? 0) + (family_links?.parents.length ?? 0);

  return (
    <div>
      {backLink}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">
            {user.user_name || "(no name)"}
          </h2>
          <p className="text-sm text-text-muted">{user.email}</p>
        </div>
        <div className="ml-auto flex gap-2">
          <RoleBadge role={user.role_id} />
          <StatusBadge status={user.user_status} />
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Left: profile + admin controls */}
        <div className="flex flex-col gap-5 lg:col-span-1">
          <Card className="p-5">
            <h3 className="mb-2 text-sm font-semibold text-text">Profile</h3>
            <dl className="divide-y divide-border">
              <InfoRow label="User ID" value={<span className="font-mono text-xs">{user.user_id}</span>} />
              <InfoRow label="Org" value={user.org_id} />
              <InfoRow label="Class" value={user.class_id} />
              <InfoRow label="Phone" value={user.phone} />
              <InfoRow label="School" value={user.school_name} />
              <InfoRow label="City" value={user.city} />
              <InfoRow label="Joined" value={user.created_at ? formatDate(user.created_at) : "--"} />
              <InfoRow
                label="Last login"
                value={user.last_login_at ? formatDate(user.last_login_at) : "Never"}
              />
            </dl>
          </Card>

          <Card className="p-5">
            <h3 className="mb-3 text-sm font-semibold text-text">Admin actions</h3>
            <div className="flex flex-col gap-4">
              <StatusControl user={user} />
              <RoleControl user={user} />
            </div>
          </Card>
        </div>

        {/* Right: subs + payments */}
        <div className="flex flex-col gap-5 lg:col-span-2">
          <Card className="p-5">
            <h3 className="mb-2 text-sm font-semibold text-text">
              Active subscriptions
              <span className="ml-2 text-text-muted">({activeSubs.length})</span>
            </h3>
            {activeSubs.length === 0 ? (
              <p className="py-2 text-sm text-text-muted">No active subscriptions.</p>
            ) : (
              <div>
                {activeSubs.map((s) => (
                  <SubscriptionRow key={s.subscription_id} sub={s} />
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="mb-2 text-sm font-semibold text-text">
              Recent payments
              <span className="ml-2 text-text-muted">({recent_payments.length})</span>
            </h3>
            {recent_payments.length === 0 ? (
              <p className="py-2 text-sm text-text-muted">No payments on record.</p>
            ) : (
              <div>
                {recent_payments.map((p) => (
                  <PaymentRow key={p.payment_id} payment={p} />
                ))}
              </div>
            )}
          </Card>

          {/* Family links (Phase 8) -- who this user is linked to in the
              parent portal, either direction. */}
          <Card className="p-5">
            <h3 className="mb-2 text-sm font-semibold text-text">
              Family links
              <span className="ml-2 text-text-muted">({familyCount})</span>
            </h3>
            {familyCount === 0 ? (
              <p className="py-2 text-sm text-text-muted">No parent-portal links.</p>
            ) : (
              <dl className="divide-y divide-border">
                {(family_links?.children ?? []).map((c) => (
                  <InfoRow
                    key={c.student_user_id}
                    label={`Child (${c.relationship})`}
                    value={
                      <Link to={`/admin/users/${c.student_user_id}`} className="font-mono text-xs text-brand hover:underline">
                        {c.student_user_id}
                      </Link>
                    }
                  />
                ))}
                {(family_links?.parents ?? []).map((p) => (
                  <InfoRow
                    key={p.parent_user_id}
                    label={`Parent (${p.relationship})`}
                    value={
                      <Link to={`/admin/users/${p.parent_user_id}`} className="font-mono text-xs text-brand hover:underline">
                        {p.parent_user_id}
                      </Link>
                    }
                  />
                ))}
              </dl>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
